import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import autobind from 'core-decorators/lib/autobind';
import debounce from 'core-decorators/lib/debounce';
import { connect } from 'react-redux';

import { getNewRelic } from 'shared/analytics/new-relic';

import {
  isClassicLayoutHeaderVisible,
  isClassicLayoutSearchBarVisible,
  isSearchInputVisible,
  isSignInVisible,
  isCategoriesDropdownVisible,
} from 'shared/utils/layout-helpers';
import { WixConnect } from 'shared/utils/wix-connect';
import WixSDK from 'shared/utils/wix-sdk';
import { storeForReload } from 'widget/utils/reload';
import { isMobile, isTablet } from 'shared/utils/device/screen';
import {
  openFullScreenChannelOverlay,
  openFullScreenSubscribeOverlay,
  openFullScreenMemberOnlyOverlay,
} from 'shared/utils/open-overlay';
import {
  listThumbnailSize,
  videosPerPage,
  classicLayoutWidgetHeight,
  MIN_WIDGET_WIDTH,
  MAX_WIDGET_WIDTH,
} from 'widget/utils/videos-sizes';


import { selectVideo, resetSelectedVideo } from 'widget/redux/client/actions/select-video';
import { playVideo, pauseVideo } from 'widget/redux/client/actions/change-playback-status';
import { getCurrentSiteUser } from 'widget/selectors/current-site-user';
import { updateSearch, resetSearch } from '../../../redux/client/actions/search';

import { getMainVideoId } from 'widget/ui-selectors/selected-video-id';
import { getAllSettings } from 'shared/selectors/app-settings';
import { getVideosGroupedByIds } from 'shared/selectors/videos';
import { isVideoPlayingOptimistic, isVideoPausedOptimistic } from 'widget/selectors/video-playback-status';
import { getSearchQuery, getSearchQueryField } from '../../../../shared/selectors/search';
import bi from 'shared/bi/bi';

import { DATA_KEY } from 'widget/data-components/paginated-channel-videos';
import { PAYMENT_TYPE } from 'shared/constants/payments';

import viewModes from 'shared/constants/view-modes';

import BaseView from 'widget/views/_base';

import DesktopPlayer from 'widget/components/players/desktop-player';
import MobilePlayer from 'widget/components/players/mobile-player';
import { isPreviewMode, openPreviewPrompt } from 'widget/components/preview-prompt/preview-prompt';
import ShareChannel from 'widget/components/share-channel/share-channel';
import AccountButton from 'widget/components/account/account-button/account-button';
import ChannelInfoButton from 'widget/components/channel-actions/channel-info-button';
import ChannelSubscriptionButton from 'widget/components/channel-actions/channel-subscription-button';
import Categories from 'widget/components/categories/categories';
import Tags from 'widget/components/tags/tags';
import { ClassicSearchBar } from 'widget/components/search-bar/search-bar';
import Videos from 'widget/views/desktop/classic/components/videos/videos';
import OverlayClassicCenter from 'widget/components/player-overlay/classic/center';
import OverlayMemberOnly from 'widget/components/player-overlay/classic/member-only';
import { WidgetScrollbarWrapper } from 'shared/components/scrollbar-wrapper/scrollbar-wrapper';
import UpdateWidgetSize from 'widget/data-components/update-widget-size';
import PaymentEvents from 'shared/components/payment-events/payment-events';
import EmptySearchResults from 'widget/components/empty-search-results/empty-search-results';
import ReloadStorage from 'widget/components/reload-storage/reload-storage';

import styles from './classic.styl';

import widgetTranslations from 'widget/translations';

const DEFAULT_STATE = {
  isInFullScreen: false,
  isTagFocused: false,
  isSearchInputFocused: false,
  searchLayout: false,
  currentCategory: null,
};


@connect((state, ownProps) => ({
  mainVideoId: getMainVideoId(state, DATA_KEY),
  isVideoPlaying: isVideoPlayingOptimistic(state),
  isVideoPaused: isVideoPausedOptimistic(state),
  currentChannelId: state.currentChannelId,
  canShowChannelCover: !state.isVideosTouched,
  appSettings: getAllSettings(state),
  currentSiteUser: getCurrentSiteUser(state),
  firstChannelVideoId: state.firstChannelVideoId,

  videoIdsByPageNumber: ownProps.paginatedChannelVideos.idChunks,
  currentVideosPageNumber: ownProps.paginatedChannelVideos.currentUIPage,
  prevVideoId: ownProps.nextChannelVideos.prevId,
  nextVideoId: ownProps.nextChannelVideos.nextId,
  goToPrevVideosPage: ownProps.paginatedChannelVideos.goBack,
  goToNextVideosPage: ownProps.paginatedChannelVideos.goForward,
  goToFirstVideosPage: ownProps.paginatedChannelVideos.toFirstPage,
  videoByIds: getVideosGroupedByIds(state),
  videosPerPage: ownProps.paginatedChannelVideos.ids && ownProps.paginatedChannelVideos.ids.length,
  fetchVideos: ownProps.paginatedChannelVideos.fetch,
  isVideosFetching: ownProps.paginatedChannelVideos.isFetching,

  isSignInVisible: isSignInVisible(state.appSettings),
  isSearchBarVisible: isClassicLayoutSearchBarVisible(state.appSettings, ownProps.channelData),
  isSearchInputVisible: isSearchInputVisible(state.appSettings, ownProps.channelData),
  isCategoriesDropdownVisible: isCategoriesDropdownVisible(state.appSettings, ownProps.channelData),

  searchQuery: getSearchQuery(state),
  searchQueryField: getSearchQueryField(state),
}), {
  selectVideo,
  resetSelectedVideo,
  playVideo,
  pauseVideo,
  updateSearch,
  resetSearch,
})
@WixConnect(styles)
export default class ClassicView extends BaseView {

  static displayName = 'ClassicView';

  static propTypes = {
    ...BaseView.propTypes,
    isSearchBarVisible: React.PropTypes.bool,
    isSearchInputVisible: React.PropTypes.bool,
    isCategoriesDropdownVisible: React.PropTypes.bool,
    isSignInVisible: React.PropTypes.bool,
    videosPerPage: React.PropTypes.number,
    searchQuery: React.PropTypes.string,
    searchQueryField: React.PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.state = {
      ...DEFAULT_STATE,
      searchBarValue: props.searchQuery,
      isInitialVideosLoaded: false,
    };

    getNewRelic().setCustomAttribute('layout', 'classic');
  }

  componentDidMount() {
    super.componentDidMount();
    this.setUtmostWidgetSize();
  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps);

    const { appSettings, channelData } = this.props;

    if (
      appSettings.numbers.videosInRow !== prevProps.appSettings.numbers.videosInRow ||
      appSettings.numbers.videoInfoPosition !== prevProps.appSettings.numbers.videoInfoPosition ||
      !_.isEqual(appSettings.booleans, prevProps.appSettings.booleans) ||
      channelData !== prevProps.channelData ||
      !_.isEqual(appSettings.requestedWidgetSize, prevProps.appSettings.requestedWidgetSize)
    ) {
      this.setUtmostWidgetSize();
    }
  }

  get listThumbnailSize() {
    const { appSettings } = this.props;
    const widgetWidth = appSettings.requestedWidgetSize.width;
    const videosInRow = appSettings.numbers.videosInRow;
    return listThumbnailSize(widgetWidth, videosInRow);
  }

  // TODO: move to data component
  @autobind
  @debounce
  setUtmostWidgetSize() {
    const { appSettings, channelData } = this.props;

    if (!appSettings || !channelData) {
      return;
    }

    const videosInRow = appSettings.numbers.videosInRow;
    const initialWidth = window.innerWidth;
    const initialHeight = window.innerHeight;
    const width = Math.min(Math.max(initialWidth, MIN_WIDGET_WIDTH), MAX_WIDGET_WIDTH);
    let height = initialHeight;

    const allVideosCount = channelData.videosCount;

    // what videos rows need to be displayed
    const perPage = videosPerPage({
      width,
      height,
    }, videosInRow, allVideosCount, appSettings, channelData);
    let rowsCount = Math.ceil(Math.min(perPage, allVideosCount) / videosInRow);

    if (rowsCount <= 0) {
      rowsCount = 1;
    }

    // widget height for this rows count
    height = Math.ceil(classicLayoutWidgetHeight(
      width, videosInRow, allVideosCount, rowsCount, appSettings, channelData,
    ));

    if (width !== initialWidth || height !== initialHeight) {
      if (WixSDK.Utils.getViewMode() === viewModes.EDITOR) {
        // editor
        WixSDK.resizeComponent({ width, height });
      } else {
        // viewer
        WixSDK.resizeWindow(width, height);
      }
      // fix TPA bug. TODO remove
      WixSDK.setHeight(height);
    }
  }

  @autobind
  reset() {
    super.reset();

    this.setState({
      ...DEFAULT_STATE,
      searchBarValue: '',
    });
    this.props.goToFirstVideosPage();
  }

  @autobind
  handleThumbnailClick(videoItem) {
    this.scrollToWidgetTop();
    this.selectVideo(videoItem);
  }

  @autobind
  scrollToWidgetTop() {
    const { appSettings: { compId } } = this.props;
    WixSDK.navigateToComponent(compId);
  }

  get tagList() {
    return _.get(this.props, 'channelData.statsInfo.tags') || [];
  }

  get categoriesList() {
    const categoriesStats = _.get(this.props, 'channelData.statsInfo.categories') || [];
    return _.map(categoriesStats, 'value');
  }

  get tags() {
    const { searchLayout, searchBarValue } = this.state;
    const tags = this.tagList;

    if (!tags.length || !searchLayout || searchBarValue) {
      return null;
    }

    return (
      <div styleName="tags-wrapper">
        <WidgetScrollbarWrapper>
          <Tags styleName="tags"
                onTagFocus={this.handleTagsFocus}
                onTagBlur={this.handleTagsBlur}
                onTagClick={this.searchByTag}
                tags={tags} />
        </WidgetScrollbarWrapper>
      </div>
    );
  }

  get noSearchResults() {
    const { searchBarValue, searchLayout } = this.state;

    if (!this.hasNoSearchResults || (searchLayout && !searchBarValue)) {
      return null;
    }

    return <EmptySearchResults />;
  }

  @autobind
  handleSlideToPrev() {
    bi.logWidgetSystem('videoList.changePage.requested');
    this.props.goToPrevVideosPage();
  }

  @autobind
  handleSlideToNext() {
    bi.logWidgetSystem('videoList.changePage.requested');
    this.props.goToNextVideosPage();
  }

  get videosList() {
    const {
      channelData,
      searchQuery,
      currentSiteUser,
      videoIdsByPageNumber,
      currentVideosPageNumber,
      videosPerPage,
      isVideosFetching,
    } = this.props;

    const { searchLayout } = this.state;

    const searchBlockShown = searchLayout && !searchQuery && this.tagList.length;

    const noVideosLoaded = !videoIdsByPageNumber.length;

    if (!isVideosFetching && channelData.videosCount === 0) {
      return (
        <div styleName="coming-soon">
          <FormattedMessage {...widgetTranslations.thisChannelIsComingSoon} />
        </div>
      );
    }

    if (isVideosFetching && noVideosLoaded || !this.mainVideo || searchBlockShown) {
      return null;
    }

    return (
      <Videos styleName="videos"
              containerWidth={this.playerSize.width}
              videoIdsByPageNumber={videoIdsByPageNumber}
              currentVideosPageNumber={currentVideosPageNumber}
              onSlideToNext={this.handleSlideToNext}
              onSlideToPrev={this.handleSlideToPrev}
              channelData={channelData}
              currentSiteUser={currentSiteUser}
              videosPerPage={videosPerPage}
              onThumbnailClick={this.handleThumbnailClick}
              thumbnailSize={this.listThumbnailSize}
              isSelectedVideoIndicationDisabled={this.isChannelCoverShown} />
    );
  }

  get search() {
    const { isSearchInputVisible, searchQuery } = this.props;

    if (!isSearchInputVisible) {
      return null;
    }

    const value = this.isCategorySelected ? '' : searchQuery;

    return (
      <ClassicSearchBar value={value}
                        onFocus={this.handleSearchInputFocus}
                        onBlur={this.handleSearchInputBlur}
                        onChange={this.setSearchBarValue}
                        onSearch={this.searchByQuery}
                        onClose={this.clearSearch}
                        styleName="search" />
    );
  }

  get categories() {
    const { isCategoriesDropdownVisible, searchQuery } = this.props;

    if (!isCategoriesDropdownVisible) {
      return null;
    }

    const { searchLayout, currentCategory, searchBarValue } = this.state;

    if (
      searchLayout ||
      (!this.isCategorySelected && (searchBarValue || searchQuery))
    ) {
      return null;
    }

    return (
      <Categories styleName="categories"
                  list={this.categoriesList}
                  onCategorySelect={this.searchByCategory}
                  selectedCategory={currentCategory} />
    );
  }

  get content() {
    const styleNames = classnames('content', {
      'no-search-bar': !this.props.isSearchBarVisible,
    });

    return (
      <section styleName={styleNames}
               className="qa-widget-content">
        <div styleName="search-line"
             className="qa-widget-search-line">
          {this.search}
          {this.categories}
        </div>
        {this.tags}
        {this.videosList}
        {this.noSearchResults}
      </section>
    );
  }

  get channelTitle() {
    const { channelData, appSettings } = this.props;
    return appSettings.booleans.showChannelTitle ? channelData.title : null;
  }

  @autobind
  showChannelInfo(event) {
    event.preventDefault();
    openFullScreenChannelOverlay(this.props.channelData.id);
  }

  @autobind
  openSubscription() {
    if (isPreviewMode()) {
      openPreviewPrompt();
      return;
    }

    openFullScreenSubscribeOverlay(this.props.channelData.id, () => {
      this.props.biEvent('widget.subscriptionPopUp.Completed', { result: 'canceled', errorMsg: '' });
    });
  }

  @autobind
  openMemberOnly(id) {
    openFullScreenMemberOnlyOverlay(this.props.channelData.id, id);
  }

  @autobind
  storeDataForReload() {
    storeForReload({
      selectedVideoId: this.props.mainVideoId,
    });
  }

  @autobind
  processDataFromStorage({ selectedVideoId, autoplay, openedModalType }) {
    if (autoplay) {
      this.playVideo({ id: selectedVideoId });
    } else {
      this.selectVideo({ id: selectedVideoId });
    }

    const modalOpener = {
      [PAYMENT_TYPE.PURCHASE]: this.openPurchase,
      [PAYMENT_TYPE.SUBSCRIPTION]: this.openSubscription,
    }[openedModalType];

    if (modalOpener) {
      modalOpener();
    }
  }

  get signInButton() {
    const { isSignInVisible, channelData } = this.props;

    if (!isSignInVisible) {
      return null;
    }

    return (
      <AccountButton styleName="action"
                     className="qa-widget-channel-account"
                     tooltipSide="bottom"
                     infoButtonClassName={styles['account-button']}
                     channelData={channelData}
                     onClick={this.openAccountInfoAtOverlay}
                     onLoginClick={this.showLogInForm}
                     onLogoutClick={this.logOut}
                     onSubscribeClick={this.openSubscription}
                     onSubscriptionCancelClick={this.processSubscriptionCancel} />
    );
  }

  get header() {
    const { appSettings, channelData } = this.props;
    const styleNames = classnames('header', {
      'empty-header': !isClassicLayoutHeaderVisible(appSettings, channelData),
    });
    const channelTitle = this.channelTitle;

    return (
      <section styleName={styleNames}
               className="qa-widget-header">
        <div styleName="channel-name"
             title={channelTitle}>
          {channelTitle}
        </div>
        <ShareChannel styleName="action"
                      channelData={channelData} />
        <ChannelInfoButton styleName="action"
                           channelData={channelData}
                           onClick={this.showChannelInfo} />
        <ChannelSubscriptionButton styleName="action"
                                   channelData={channelData}
                                   onClick={this.openSubscription} />
        {this.signInButton}
      </section>
    );
  }

  get player() {
    const videoItem = this.mainVideo;

    if (videoItem) {
      return this.renderPlayerForCurrentDevice(videoItem);
    }
    return this.noVideosPlayerContent;
  }

  renderPlayerForCurrentDevice(videoItem) {
    const { channelData, isVideoPlaying } = this.props;
    const commonProps = {
      styleName: 'player',
      className: 'qa-widget-player',
      videoItem,
      channelData,
      ...this.playerSize,
      paused: !isVideoPlaying,
      canShowChannelCover: this.canShowChannelCover,
      onPurchaseClick: this.openPurchase,
      onSubscribeClick: this.openSubscription,
      openPreviewPrompt,
      onMemberSignUp: this.openMemberOnly,
      memberOnlyOverlay: <OverlayMemberOnly />,
      children: this.playerOverlay,
      onFullScreenChanged: this.setFullScreenState,
    };

    const PlayerComponent = isMobile() || isTablet() ?
      MobilePlayer :
      DesktopPlayer;

    return <PlayerComponent {...commonProps} />;
  }

  get noVideoPlayerOverlay() {
    const { appSettings, channelData } = this.props;

    const props = {
      appSettings,
      channelData,
      showChannelCover: true,
      videoItem: {},
    };

    return (
      <OverlayClassicCenter {...props} />
    );
  }

  get playerOverlay() {
    const videoItem = this.mainVideo;
    const { appSettings, channelData } = this.props;
    const props = {
      appSettings,
      channelData,
      videoItem,
      showChannelCover: this.canShowChannelCover,
      nextVideoId: this.props.nextVideoId,
      playSelectedVideo: this.playSelectedVideo,
      playNextVideo: this.playNextVideo,
    };

    return (
      <OverlayClassicCenter {...props} />
    );
  }

  render() {
    if (!this.isUIBlockingDataLoaded) {
      return this.spinner;
    }

    const { appSettings } = this.props;
    const widgetSize = _.get(appSettings, 'requestedWidgetSize');

    return (
      <section styleName="container"
               style={widgetSize}
               className="qa-widget-container"
               data-channel-layout="classic">
        <UpdateWidgetSize disabled={this.state.isInFullScreen} />
        <ReloadStorage onReload={this.processDataFromStorage} />

        {this.header}
        {this.player}
        {this.content}

        <PaymentEvents onRent={this.setCurrentVideoFromPayment}
                       onSale={this.setCurrentVideoFromPayment}
                       onSubscription={this.reset}
                       onSubscriptionCancel={this.reset} />
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/views/desktop/classic/classic.js